import React, { useCallback, useEffect, useState } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import PrivateComponentLoadingPage from "../LoadingPage/PrivateComponentLoadingPage";
import PaymentsAppLayout from "../../Components/Molecules/PaymentsAppLayout";
import { useAdminStore } from "../../Store/Admin";
import { useParams } from "react-router-dom";
import { usePagination, PAGINATION_KEYS } from "../../Store/Pagination";
import { AdyenTransferDetailsView } from "../../Contracts/Admin";
import Table, { Column, DataSourceItem } from "../../Components/Atoms/Table";
import { toLocalDateString } from "../../Utils/DateUtils";
import { AdyenTransactionableType } from "../../Contracts/Transaction";
import { useTeamStore } from "../../Store/Team";
import { capitalizeAfterSpace } from "../../Contracts/Common";
import LoadingPage from "../LoadingPage";
import TablePaginationFooter from "../../Components/Atoms/Table/TablePaginationFooter";
import { createSortFunction } from "../../Utils/TableUtils";

const AdminTeamTransfersListPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { team_id } = useParams();
  const { limit, page, setLimit, setPage } = usePagination(
    PAGINATION_KEYS.ADMIN_TEAM_TRANSFER_LIST,
    {
      limit: 25,
      page: 1
    }
  );
  const [sortParamValue, setSortParamValue] = useState<string>("created_at:desc");
  const {
    isLoading,
    teamDetails,
    getOneTeam,
    getTeamTransfers,
    teamTransfers,
    manualPayoutOfTransfers,
    teamTransfersCount
  } = useAdminStore();
  const { formatCurrency } = useTeamStore();
  const [selectedTransfers, setSelectedTransfers] = React.useState<
    DataSourceItem<AdyenTransferDetailsView>[]
  >([]);
  const onLoad = useCallback(async () => {
    if (team_id) {
      await Promise.all([
        getOneTeam(getAccessTokenSilently, team_id),
        getTeamTransfers(getAccessTokenSilently, team_id, limit, sortParamValue, page)
      ]);
    } else {
      console.error("No team id found");
    }
  }, [team_id, getAccessTokenSilently, getOneTeam, getTeamTransfers, limit, sortParamValue, page]);
  useEffect(() => {
    onLoad();
  }, [onLoad]);
  const columns: Column<AdyenTransferDetailsView>[] = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status"
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category"
    },
    {
      title: "Transferred At",
      dataIndex: "transferredAt",
      key: "transferredAt",
      sortClicked: createSortFunction("created_at", setSortParamValue),
      defaultSortOrder: "desc"
    },
    {
      title: "Transfer Id",
      dataIndex: "transferId",
      key: "transferId"
    },
    {
      title: "Transaction PSP Reference",
      dataIndex: "transactionPSPReference",
      key: "transactionPSPReference"
    },
    {
      title: "Transaction Status",
      dataIndex: "transactionStatus",
      key: "transactionStatus"
    },
    {
      title: "Transactionable Type",
      dataIndex: "transactionableType",
      key: "transactionableType"
    },
    {
      title: "Invoice/Quote Id",
      dataIndex: "invoiceQuoteId",
      key: "invoiceQuoteId"
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount"
    },
    {
      title: "Direction",
      dataIndex: "direction",
      key: "direction"
    },
    {
      title: "Payout Transfer Id",
      dataIndex: "payoutTransferId",
      key: "payoutTransferId"
    },
    {
      title: "Payout Status",
      dataIndex: "payoutStatus",
      key: "payoutStatus"
    }
  ];
  const dataSource: DataSourceItem<AdyenTransferDetailsView>[] =
    teamTransfers?.transfers?.data.map(transfer => ({
      key: transfer.transfer.id.toString(),
      status: transfer.transfer.status,
      category: transfer.transfer.category,
      transferredAt: toLocalDateString(transfer.transfer.value_date, true),
      transferId: transfer.transfer.transfer_id,
      transactionPSPReference: transfer.transfer.psp_reference || "-",
      transactionStatus: transfer.transaction?.status || "-",
      transactionableType:
        transfer.transaction?.transactionable_type === AdyenTransactionableType.CollectibleInvoice
          ? "Invoice"
          : transfer.transaction?.transactionable_type ===
              AdyenTransactionableType.PaymentsGrowQuote
            ? "Quote"
            : "-",
      invoiceQuoteId: transfer.invoice_or_quote_id || "-",
      amount: formatCurrency(transfer.transfer.amount, transfer.transfer.currency),
      direction: capitalizeAfterSpace(transfer.transfer.direction),
      payoutTransferId: transfer.transfer.payments_payout_id || "-",
      payoutStatus: transfer.transfer.status,
      data: transfer
    })) || [];
  return (
    <PaymentsAppLayout>
      <div className="flex flex-col h-[calc(100vh-9rem)] w-[calc(100vw-10rem)]">
        {isLoading ? (
          <div className="h-6 w-60 animate-pulse bg-gray-300 rounded" />
        ) : (
          teamDetails && (
            <div className="text-xl font-semibold">Transfers for {teamDetails?.name}</div>
          )
        )}
        <div className="flex flex-grow flex-col overflow-auto">
          {isLoading && !teamTransfers ? (
            <LoadingPage />
          ) : (
            <Table
              columns={columns}
              dataSource={dataSource}
              isLoading={isLoading}
              rowSelectionOptions={{
                enableSelection: true,
                selectionEnabled: d => d.data.allow_payout,
                onRowSelectionChange: (_, selectedRows) => setSelectedTransfers(selectedRows),
                selected: selectedTransfers.map(transfer => transfer.key)
              }}
            />
          )}
        </div>
        <div className="flex flex-row justify-between items-center align-middle">
          <div>
            Amount to be Paid out:{" "}
            {formatCurrency(
              selectedTransfers.reduce((acc, transfer) => acc + transfer.data.transfer.amount, 0)
            )}
          </div>
          <div className="flex justify-end pt-3 pr-3 border-t">
            <button
              className={`text-white px-4 py-2 rounded ${
                selectedTransfers.length === 0
                  ? "bg-gray-200 cursor-not-allowed"
                  : "bg-blue-500 cursor-pointer"
              }`}
              disabled={selectedTransfers.length === 0}
              onClick={async () => {
                if (team_id) {
                  if (window.confirm("Are you sure you want to create payout?")) {
                    await manualPayoutOfTransfers(
                      getAccessTokenSilently,
                      team_id,
                      selectedTransfers.map(transfer => transfer.data.transfer.id),
                      limit,
                      sortParamValue,
                      page
                    );
                    setSelectedTransfers([]);
                  }
                } else {
                  console.error("No team id found");
                }
              }}
            >
              Create Payout
            </button>
          </div>
          <div className="flex flex-col ml-4">
            <div className="flex space-x-2 self-end font-semibold">
              <div>Actual Balance:</div>
              {teamTransfers?.actual_balance && (
                <div>{formatCurrency(teamTransfers.actual_balance)}</div>
              )}
            </div>
            <div className="flex space-x-2 self-end font-semibold">
              <div>Calculated Balance:</div>
              {teamTransfers?.calculated_balance && (
                <div>{formatCurrency(teamTransfers.calculated_balance)}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <TablePaginationFooter
        limit={limit}
        setLimit={setLimit}
        page={page}
        setPage={setPage}
        totalRecords={teamTransfersCount}
        isLoading={isLoading}
      />
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(AdminTeamTransfersListPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
